<template>
    <div
        :id="`process-element-${element.id}`"
        :style="containerStyles"
        class="process-element-overlay"
        ref="processElementOverlay"
        v-if="currentEntityType !== EntityType.FLOW"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        @mousemove="handleMouseMove"
    >
        <!-- Always render the top level div, since it defines the width -->
        <Popup
            v-if="
                !isEditing &&
                currentEntityId &&
                currentEntityType &&
                isHoverPopupEnabled &&
                businessObject.processElementId
            "
            :anchor="processElementOverlayElement!"
        >
            <GenericCard
                v-if="isHoverPopupEnabled"
                :entityId="currentEntityId"
                :entityType="currentEntityType"
                :showPath="false"
                :entityRefs="enrichedEntityRefs"
                :generatesEvidence="taskIndicators.generatesEvidence"
                :evidenceInstructions="taskIndicators.evidenceInstructions"
                :documentCount="taskIndicators.documentCount"
            />
        </Popup>
        <RouterLink
            v-if="shouldRenderLink"
            :id="`process-element-link-${element.id}`"
            class="process-element-link"
            :class="{
                hover: isHovering,
            }"
            :to="routerLink"
            :aria-label="currentElement?.name ?? businessObject.name"
        />
        <div
            v-else
            :id="`process-element-link-${element.id}`"
            class="process-element-link disabled"
        ></div>
    </div>
</template>

<script setup lang="ts">
import { type Element as BpmnElement } from "bpmn-js/lib/model/Types";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import { computed, ref, toRef, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import Popup from "@/components/Popup.vue";
import { useBpmnManager } from "@/composables/bpmn-manager";
import * as bpmn_helpers from "@/composables/bpmn-manager/helpers";
import { useEditManager } from "@/composables/edit-manager";
import { decodeArtefactPreview } from "@/lib/artefacts/preview";
import { ProcessElementType, ProcessTask } from "@/lib/openapi/gen";
import { withThreadRevision } from "@/lib/thread-revision-link";
import { useBusinessEntitiesManager } from "@/stores/business-entities-store";
import { EntityType } from "@/stores/entity-store";
import {
    ProcessElement,
    useProcessElementsManager,
} from "@/stores/process-elements-store";

const props = withDefaults(
    defineProps<{
        element: BpmnElement;
        bpmnManager: ReturnType<typeof useBpmnManager>;
        isHoverPopupEnabled?: boolean;
    }>(),
    {
        isHoverPopupEnabled: true,
    }
);

const element = toRef(props.element);
const { processElements } = useProcessElementsManager();
const { entities } = useBusinessEntitiesManager();
const { isEditing } = useEditManager();
const isHovering = ref(false);
const route = useRoute();
const processElementOverlayElement = useTemplateRef("processElementOverlay");
const businessObject = computed(() => getBusinessObject(element.value));

const containerStyles = computed(() => {
    // Add padding to the element container to overlay BPMN element border
    return {
        "width": element.value.width + 16 + "px",
        "height": element.value.height + 16 + "px",
    };
});

/**
 * Get the current element from the processElements list.
 * Falls back to lookup by bpmn_id for tasks not yet embedded with a processElementId
 * in the BPMN XML (e.g. immediately after adding a new task and syncing).
 */
const currentElement = computed<ProcessElement | undefined>(() => {
    const bo = businessObject.value;
    if (bo.processElementId) {
        return processElements.value?.find(
            (t) => t.identity === bo.processElementId
        );
    }
    return processElements.value?.find((t) => t.bpmn_id === bo.id);
});

const currentEntityId = computed(() => {
    const bo = businessObject.value;
    return (
        bo.processElementId ||
        bo.processId ||
        bo.calledElementId ||
        currentElement.value?.identity
    );
});

const currentEntityType = computed(() => {
    const bo = businessObject.value;
    if (bo.processElementId || currentElement.value) {
        const type = bpmn_helpers.elementType(element.value);
        return type ? PROCESS_ELEMENT_ENTITY_TYPE_MAP[type] : undefined;
    } else if (bo.processId) {
        return EntityType.PROCESS;
    }
    return bo.calledElementType as EntityType | undefined;
});

const PROCESS_ELEMENT_ENTITY_TYPE_MAP = {
    [ProcessElementType.TASK]: EntityType.TASK,
    [ProcessElementType.EVENT]: EntityType.EVENT,
    [ProcessElementType.GATEWAY]: EntityType.GATEWAY,
    [ProcessElementType.FLOW]: EntityType.FLOW,
};

const enrichedEntityRefs = computed(() => {
    const refs = currentElement.value?.entity_refs ?? [];
    return refs.map((ref) => ({
        ...ref,
        entityName:
            entities.value?.find((e) => e.identity === ref.entity_id)?.name ??
            ref.entity_id.slice(0, 8) + "…",
    }));
});

const taskIndicators = computed(() => {
    const task =
        currentEntityType.value === EntityType.TASK
            ? (currentElement.value as ProcessTask | undefined)
            : undefined;
    return {
        generatesEvidence: task?.generates_evidence ?? false,
        evidenceInstructions: decodeArtefactPreview(
            task?.evidence_instructions?.preview
        ),
        documentCount: task?.document_count ?? 0,
    };
});

const routerLink = computed(() => {
    // Validate params exist before creating route
    if (!route.params.orgName || !route.params.stekName) {
        return {};
    }

    const navigationRoute = bpmn_helpers.getNavigationRoute(
        element.value,
        route.params.orgName as string,
        route.params.stekName as string
    );
    if (navigationRoute) return withThreadRevision(navigationRoute);

    // Fallback: use the processElements store identity for tasks not yet
    // embedded with a processElementId in the BPMN XML.
    const processElementId = currentElement.value?.identity;
    if (processElementId) {
        const elType = bpmn_helpers.elementType(element.value);
        if (elType) {
            return withThreadRevision({
                name: "processElementPage",
                params: {
                    orgName: route.params.orgName,
                    stekName: route.params.stekName,
                    elementId: processElementId,
                    elementType: elType,
                },
            });
        }
    }
    return {};
});

const shouldRenderLink = computed(() => {
    return Boolean(routerLink.value.name && route.params.stekName);
});

const handleMouseMove = (event: MouseEvent) => {
    props.bpmnManager.eventBus?.fire("element.mousemove", {
        element: element.value,
        gfx: undefined,
        originalEvent: event,
        type: undefined,
    });
};

const handleMouseEnter = (event: MouseEvent) => {
    isHovering.value = true;
    props.bpmnManager.eventBus?.fire("element.hover", {
        element: element.value,
        gfx: props.bpmnManager.elementRegistry?.getGraphics(element.value),
        originalEvent: event,
        type: undefined,
    });
};

const handleMouseLeave = (event: MouseEvent) => {
    isHovering.value = false;
    props.bpmnManager.eventBus?.fire("element.out", {
        element: element.value,
        gfx: props.bpmnManager.elementRegistry?.getGraphics(element.value),
        originalEvent: event,
        type: undefined,
    });
};
</script>

<style scoped lang="scss">
.process-element-link {
    text-decoration: none;
    padding: 8px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;

    // Give the text same properties as the BPMN text.
    color: black;
    font-family: Arial, sans-serif;
    line-height: 1.2;
    font-size: 12px;

    &.hover {
        color: white;
    }

    * {
        max-width: 90%;
        text-wrap-mode: balance;
        overflow-wrap: break-word;
    }
}
</style>
